class ScrollingTrust extends HTMLElement {
  constructor() {
    super();

    this.container = this.querySelector(".scrolling-trust__container");
    this.items = this.querySelectorAll(".scrolling-trust__item");
    this.speed = parseInt(this.getAttribute("data-scrolling-speed")) || 100;
    this.pauseOnHover = this.getAttribute("data-pause-on-hover") === "true";

    this.isAnimating = false;
    this.animationId = null;
    this.currentPosition = 0;
    this.itemWidth = 0;
    this.totalWidth = 0;

    this.init();
  }

  init() {
    if (this.items.length === 0) return;

    this.setupStyles();
    this.calculateDimensions();
    this.duplicateItems();
    this.setupEventListeners();
    this.startAnimation();
  }

  setupStyles() {
    this.style.overflow = "hidden";
    this.style.whiteSpace = "nowrap";

    if (this.container) {
      this.container.style.display = "flex";
      this.container.style.alignItems = "center";
      this.container.style.gap = "2rem";
      this.container.style.willChange = "transform";
    }

    this.items.forEach((item) => {
      item.style.flexShrink = "0";
      item.style.display = "inline-flex";
      item.style.alignItems = "center";
      item.style.justifyContent = "center";
    });
  }

  calculateDimensions() {
    if (!this.container || this.items.length === 0) return;

    this.container.style.transform = "translateX(0)";

    let totalWidth = 0;
    this.items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      totalWidth += itemRect.width;
      if (index < this.items.length - 1) {
        totalWidth += 32;
      }
    });

    this.totalWidth = totalWidth;
    this.itemWidth = totalWidth / this.items.length;
  }

  duplicateItems() {
    if (!this.container || this.items.length === 0) return;

    const containerWidth = this.getBoundingClientRect().width;
    const duplicationsNeeded = Math.ceil(containerWidth / this.totalWidth) + 2;

    for (let i = 0; i < duplicationsNeeded; i++) {
      this.items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        this.container.appendChild(clone);
      });
    }
  }

  setupEventListeners() {
    if (this.pauseOnHover) {
      this.addEventListener("mouseenter", () => this.pauseAnimation());
      this.addEventListener("mouseleave", () => this.resumeAnimation());
    }

    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 250);
    });
  }

  handleResize() {
    this.stopAnimation();

    const originalItemsCount = this.items.length;
    const allItems = this.container.querySelectorAll(".scrolling-trust__item");
    for (let i = originalItemsCount; i < allItems.length; i++) {
      allItems[i].remove();
    }

    this.calculateDimensions();
    this.duplicateItems();
    this.currentPosition = 0;
    this.startAnimation();
  }

  startAnimation() {
    if (this.isAnimating || !this.container) return;

    this.isAnimating = true;
    this.animate();
  }

  animate() {
    if (!this.isAnimating) return;

    const pixelsPerSecond = this.speed;
    const pixelsPerFrame = pixelsPerSecond / 60;

    this.currentPosition -= pixelsPerFrame;

    if (Math.abs(this.currentPosition) >= this.totalWidth) {
      this.currentPosition = 0;
    }

    this.container.style.transform = `translateX(${this.currentPosition}px)`;

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  pauseAnimation() {
    this.isAnimating = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  resumeAnimation() {
    if (!this.isAnimating) {
      this.startAnimation();
    }
  }

  stopAnimation() {
    this.pauseAnimation();
    this.currentPosition = 0;
    if (this.container) {
      this.container.style.transform = "translateX(0)";
    }
  }

  pause() {
    this.pauseAnimation();
  }

  play() {
    this.resumeAnimation();
  }

  setSpeed(newSpeed) {
    this.speed = parseInt(newSpeed) || 100;
  }

  disconnectedCallback() {
    this.stopAnimation();
    window.removeEventListener("resize", this.handleResize);
  }
}

if (customElements && !customElements.get("scrolling-trust")) {
  customElements.define("scrolling-trust", ScrollingTrust);
}

document.addEventListener("DOMContentLoaded", () => {
  if (!customElements) {
    const scrollingTrustElements = document.querySelectorAll("scrolling-trust");
    scrollingTrustElements.forEach((element) => {
      new ScrollingTrust.prototype.constructor.call(element);
    });
  }
});
